import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { LocaleContext } from './LocaleContext';
import type { LocaleContextValue } from './LocaleContext';
import type { SupportedLocale } from './locales';
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from './locales';

function readStoredLocale(): SupportedLocale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as SupportedLocale;
    }
  } catch {
    /* storage unavailable */
  }
  return null;
}

function persistLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage unavailable */
  }
}

/** Provides locale state, setLocale, and i18nReady flag. */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<SupportedLocale>(
    () => readStoredLocale() ?? (i18n.language as SupportedLocale) ?? DEFAULT_LOCALE,
  );
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized);

  // Wait for initial i18next load
  useEffect(() => {
    if (i18n.isInitialized) {
      setI18nReady(true);
      return;
    }
    const onInit = () => { setI18nReady(true); };
    i18n.on('initialized', onInit);
    return () => { i18n.off('initialized', onInit); };
  }, [i18n]);

  // Keep state in sync when i18next changes language from the outside (LanguageDetector, tests)
  useEffect(() => {
    const onLangChanged = (lng: string) => {
      if ((SUPPORTED_LOCALES as readonly string[]).includes(lng)) {
        setLocaleState(lng as SupportedLocale);
      }
    };
    i18n.on('languageChanged', onLangChanged);
    return () => { i18n.off('languageChanged', onLangChanged); };
  }, [i18n]);

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      persistLocale(next);
      void i18n.changeLanguage(next);
      setLocaleState(next);
    },
    [i18n],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, i18nReady }),
    [locale, setLocale, i18nReady],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
